/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getLogger } from './SearchLogger';

export default class PerformanceUtils {
	/**
	 * Returns the performance timing by name
	 * More information:- https://developer.mozilla.org/en-US/docs/Web/API/Performance/getEntriesByName
	 * @param  {[string]} name
	 * @returns {Array<Object>}
	 *
	 */
	static getPerformanceTimingByName(name: string) {
		let performanceTimingByName: PerformanceEntryList = [];

		try {
			if (globalThis.performance?.getEntries()) {
				performanceTimingByName = globalThis.performance.getEntriesByName(name);
			}
		} catch (e) {
			getLogger().info('PerformanceUtils', 'Failed to access performance timing by name', e);
		}

		return performanceTimingByName;
	}

	/**
	 * Returns the memory performance
	 * More information:- https://developer.mozilla.org/en-US/docs/Web/API/Performance/memory
	 * @returns {Object}
	 */
	static getMemoryPerformance() {
		const memoryPerformance = {};

		try {
			// @ts-ignore
			if (globalThis.performance?.memory) {
				// @ts-ignore
				memoryPerformance.totalJSHeapSize = globalThis.performance.memory.totalJSHeapSize;
				// @ts-ignore
				memoryPerformance.usedJSHeapSize = globalThis.performance.memory.usedJSHeapSize;
				// @ts-ignore
				memoryPerformance.jsHeapSizeLimit = globalThis.performance.memory.jsHeapSizeLimit;
			}
		} catch (e) {
			getLogger().info('PerformanceUtils', 'Failed to access memory performance', e);
		}

		return memoryPerformance;
	}

	/**
	 * Returns the navigation performance
	 * @returns {Object}
	 */
	static getNavigationPerformance() {
		let navigationPerformance = {};
		let navigationData = {};

		try {
			if (globalThis.performance?.getEntries()) {
				navigationData = globalThis.performance.getEntriesByType('navigation');
				// @ts-ignore
				if (navigationData?.length) {
					navigationPerformance = navigationData[0];
				}
			}
		} catch (e) {
			getLogger().info('PerformanceUtils', 'Failed to access navigation performance', e);
		}

		return navigationPerformance;
	}

	/**
	 * Returns the performance data reported by the browser
	 * @returns {Object}
	 */
	static getPerformanceData() {
		const performanceData: Record<string, any> = {
			firstContentfulPaint: [],
			firstPaint: [],
			memory: PerformanceUtils.getMemoryPerformance(),
			navigation: PerformanceUtils.getNavigationPerformance(),
			performance: {},
		};

		let firstPaint: PerformanceEntryList;
		let firstContentfulPaint: PerformanceEntryList;

		try {
			if (globalThis.performance?.getEntries()) {
				performanceData.performance = globalThis.performance;
				firstPaint = globalThis.performance.getEntriesByName('first-paint');
				firstContentfulPaint = globalThis.performance.getEntriesByName('first-contentful-paint');
				if (firstPaint?.length > 0) {
					performanceData.firstPaint = firstPaint[0];
				}

				if (firstContentfulPaint?.length > 0) {
					performanceData.firstContentfulPaint = firstContentfulPaint[0];
				}
			}
		} catch (e) {
			getLogger().info('PerformanceUtils', 'Failed to access performance data', e);
		}

		return performanceData;
	}
}
